/** @jsxImportSource @emotion/react */
import React, { FC, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { GuestTransactionStatus, ITransaction } from '../../../../api/transactions/types';
import { StopButton } from '../../components/stop-button/stop-button.component';
import { InfoPanel } from '../../components/info-panel/info-panel.component';
import IdleFeeBlock from '../../components/idle-fee-block/idle-fee-block.component';
import { EnergyRateLines } from '../../components/energy-rate-lines/energy-rate-lines.component';

import chargeProcessStyles from './charge-process.styles';
import { selectAppState } from '../../../../store/app/app.selector';
import { formatDateToLocaleString } from '../../../../utils/utils';
import flage from '../../../../assets/flage_inbound.svg';
import { useNavigate } from 'react-router-dom';
import { useTransactionHistory } from '../../../../hooks/useTransactionHistory';
import { EStationStatus, NO_IDLE_STATUSES } from '../../../../api/station/types';

type PropTypes = {
    guest: ITransaction;
};

export const ChargeProcess: FC<PropTypes> = ({ guest }) => {
    const intl = useIntl();
    const { transaction, station } = guest;
    const { locale } = useSelector(selectAppState);
    const navigation = useNavigate();
    const { updateRecentTransactionId } = useTransactionHistory();

    const connector = useMemo(() => {
        return station?.connectors?.at(transaction?.connectorId - 1);
    }, [transaction, station]);

    const shouldHideStopButton = guest.status === GuestTransactionStatus.Finishing;

    const showIdleBlock = !!(
        guest.pricing?.idleFee?.enabled &&
        !guest.idleStop &&
        (connector?.status === EStationStatus.SUSPENDED_EV ||
            (guest.idleStart && !NO_IDLE_STATUSES.includes(connector?.status)))
    );

    const showIdleTimer = showIdleBlock && !!guest.idleFeeCalc?.minutesTotal;

    useEffect(() => {
        updateRecentTransactionId(guest._id);
    }, []);

    return (
        <div css={chargeProcessStyles.container}>
            <div css={chargeProcessStyles.businessCardInfo}>
                <div css={chargeProcessStyles.businessCardInfoInnerContainer}>
                    <div css={chargeProcessStyles.businessCardInfoTitle}>
                        {guest?.__id
                            ? `${intl.formatMessage({ id: 'transaction' })} #${guest?.__id}`
                            : intl.formatMessage({ id: 'startingTransaction' })}
                    </div>
                    {transaction?.startAt && (
                        <div css={chargeProcessStyles.transactionDate}>
                            {formatDateToLocaleString(new Date(transaction?.startAt), locale)}
                        </div>
                    )}
                </div>
                <div css={chargeProcessStyles.arrowRight}>
                    <img onClick={() => navigation(`/`)} src={flage} alt="arrow-right" />
                </div>
            </div>
            <h4 css={chargeProcessStyles.containerTitle}>
                {connector && (
                    <div css={chargeProcessStyles.blinker}>
                        <span css={chargeProcessStyles.blinkerInner} />
                    </div>
                )}
                {connector
                    ? intl.formatMessage({
                          id:
                              guest.status === GuestTransactionStatus.Finishing
                                  ? 'connector.statuses.finishing'
                                  : `connector.statuses.${connector?.status.toLowerCase()}`,
                      })
                    : null}
            </h4>

            <InfoPanel guest={guest} charge={transaction} />
            <EnergyRateLines guest={guest} charge={transaction} />
            <div css={chargeProcessStyles.actions}>
                {showIdleBlock && <IdleFeeBlock guest={guest} showTimer={showIdleTimer} />}
                {!shouldHideStopButton && (
                    <div css={chargeProcessStyles.state}>
                        <StopButton guest={guest} />
                    </div>
                )}
            </div>
        </div>
    );
};
